import { InfoHint } from 'src/components/info-hint'
import { Button } from './button'
import { GuestSurveyReview } from './guest-survey-review'
import { FC, ReactNode, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import {
  ReservationGuest,
  TraStatus,
  GuestDeleteConflict,
  GuestDeleteMode,
  complianceStatusEnum,
  editPhaseEnum,
  guestDeleteModeEnum,
} from '../services/reservation-guests/models'
import { downloadReceipt, downloadSireForGuest } from '../utils/download'
import { useSubmitTraForGuest } from '../hooks/use-submit-tra'
import { useConfirmSireForGuest, useUnconfirmSireForGuest } from '../hooks/use-confirm-sire'
import { useIsJobActive } from '../stores/async-jobs'
import { taskTypeEnum } from '../services/async-jobs/models'
import { CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid'
import { PlayCircleIcon, EyeIcon } from '@heroicons/react/20/solid'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { Spinner } from './spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'
import { PhoneNumberInput, isValidPhone } from './phone-number-input'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  deleteGuest as deleteGuestCall,
  guestDeleteConflictFromError,
  reservationGuestApi,
  updateContact,
} from '../services/reservation-guests/api'
import { cn } from '../utils/style'
import { addDays, differenceInCalendarDays, format } from 'date-fns'
import { GuestDeleteDialog, guestDeleteTier } from './reservations/guest-delete-dialog'

import sireImg from '../assets/images/sire.png'
import { BookingDetailValues } from '../services/reservations/options'
import { MissingBookingDetailsNotice } from './reservations/missing-booking-details-notice'
import { reservationQueries } from '../services/reservations/queries'

interface Props {
  guest: ReservationGuest
  reservationId: string
  checkoutDate?: string
  onShowTra: (guest: ReservationGuest) => void
  reportsTra?: boolean
  reportsSire?: boolean
  propertyCountry?: string
  missingBookingDetails?: BookingDetailValues[]
}

const AccordionSection: FC<{
  title: string
  children: React.ReactNode
  'data-testid'?: string
}> = ({ title, children, ...props }) => {
  const [open, setOpen] = useState(false)
  return (
    <div data-testid={props['data-testid']}>
      <Button
        variant="ghost"
        tone="neutral"
        size="sm"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full justify-between text-left text-sm font-medium"
      >
        <span>{title}</span>
        <ChevronDownIcon
          className={cn([
            'h-4 w-4 text-text-placeholder motion-safe:transition-transform',
            open && 'rotate-180',
          ])}
          aria-hidden="true"
        />
      </Button>
      {open && <div className="pb-2 pt-1">{children}</div>}
    </div>
  )
}

/** Non-interactive status icon with an accessible name and a hover/focus tooltip. */
function StatusIcon({
  label,
  testId,
  children,
}: {
  label: string
  testId: string
  children: ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={label}
          tabIndex={0}
          className="inline-flex rounded focus:outline-none focus:ring-2 focus:ring-slate-300"
          data-testid={testId}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

function TraStatusIcon({ guest, isJobActive }: { guest: ReservationGuest; isJobActive: boolean }) {
  const { t } = useTranslation()
  const isSubmitting = guest.traStatus === TraStatus.submitting || isJobActive
  if (isSubmitting) return <Spinner size="xs" />
  if (guest.traStatus === TraStatus.success)
    return (
      <StatusIcon label={t('reservationGuestCard.traStatusSubmitted')} testId="guest-tra-status">
        <CheckCircleIcon className="h-4 w-4 text-green-500" aria-hidden="true" />
      </StatusIcon>
    )
  if (guest.traStatus === TraStatus.failed)
    return (
      <StatusIcon label={t('reservationGuestCard.traStatusFailed')} testId="guest-tra-status">
        <XCircleIcon className="h-4 w-4 text-error" aria-hidden="true" />
      </StatusIcon>
    )
  return (
    <StatusIcon label={t('reservationGuestCard.traStatusPending')} testId="guest-tra-status">
      <ClockIcon className="h-4 w-4 text-text-placeholder" aria-hidden="true" />
    </StatusIcon>
  )
}

function SireRetentionBanner({
  checkoutDate,
  dataScrubbed,
}: {
  checkoutDate: string
  dataScrubbed?: boolean
}) {
  const { t } = useTranslation()
  if (dataScrubbed) {
    return (
      <div
        className="rounded-md bg-surface-hover px-3 py-2 text-xs text-text-secondary"
        data-testid="sire-scrubbed-banner"
      >
        {t('reservationGuestCard.dataScrubbed')}
      </div>
    )
  }

  const deletionDate = addDays(new Date(checkoutDate), 90)
  const daysRemaining = differenceInCalendarDays(deletionDate, new Date())

  if (daysRemaining < 1) {
    return null
  }

  const formattedDate = format(deletionDate, 'MMM d, yyyy')
  const colorClasses =
    daysRemaining >= 30
      ? 'bg-yellow-50 text-yellow-800'
      : daysRemaining >= 14
      ? 'bg-orange-50 text-orange-800'
      : 'bg-error-50 text-error-800'

  return (
    <div
      className={cn(['rounded-md px-3 py-2 text-xs', colorClasses])}
      data-testid="sire-retention-banner"
      data-days-remaining={daysRemaining}
    >
      {t('reservationGuestCard.sireRetentionWarning', { date: formattedDate })}
    </div>
  )
}

function SireStatusIcon({ guest }: { guest: ReservationGuest }) {
  const { t } = useTranslation()
  if (guest.sireSubmitted)
    return (
      <StatusIcon label={t('reservationGuestCard.sireStatusSubmitted')} testId="guest-sire-status">
        <CheckCircleIcon className="h-4 w-4 text-green-500" aria-hidden="true" />
      </StatusIcon>
    )
  if (guest.needsSire)
    return (
      <StatusIcon label={t('reservationGuestCard.sireStatusPending')} testId="guest-sire-status">
        <ClockIcon className="h-4 w-4 text-yellow-500" aria-hidden="true" />
      </StatusIcon>
    )
  return null
}

export const ReservationGuestCard: FC<Props> = ({
  guest,
  reservationId,
  checkoutDate,
  onShowTra,
  reportsTra,
  reportsSire,
  propertyCountry,
  missingBookingDetails = [],
}) => {
  const { mutate: submitTra } = useSubmitTraForGuest()
  const { mutate: confirmSire, isPending: isConfirmingSire } = useConfirmSireForGuest()
  const { mutate: unconfirmSire, isPending: isUnconfirmingSire } = useUnconfirmSireForGuest()
  const { t } = useTranslation()
  const isJobActive = useIsJobActive(taskTypeEnum.TRA_SUBMISSION, guest.id)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [entryCode, setEntryCode] = useState(guest.sireEntryConfirmationCode ?? '')
  const [exitCode, setExitCode] = useState(guest.sireExitConfirmationCode ?? '')

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [isRemoved, setIsRemoved] = useState(false)
  // Set from a 409: the server's view of the guest wins over the loaded one.
  const [deleteConflict, setDeleteConflict] = useState<GuestDeleteConflict | null>(null)
  const isFiled = guest.traStatus === TraStatus.success || guest.sireSubmitted
  const receiptSystems = guest.receiptSystems ?? []
  const deleteFilings = deleteConflict?.filings ?? guest.filings ?? []
  const deleteTier = deleteConflict
    ? guestDeleteTier({
        scrubbed: deleteConflict.code === 'already_scrubbed',
        hasProof:
          deleteConflict.code === 'guest_filed' ||
          deleteConflict.filings.length > 0 ||
          deleteConflict.hasReceipts,
      })
    : guestDeleteTier({
        scrubbed: Boolean(guest.dataScrubbed),
        hasProof: isFiled || receiptSystems.length > 0,
      })

  const [contactFirstName, setContactFirstName] = useState(guest.guestRef.firstName)
  const [contactLastName, setContactLastName] = useState(guest.guestRef.lastName)
  const [contactEmail, setContactEmail] = useState(guest.guestRef.email ?? '')
  const [contactPhone, setContactPhone] = useState(guest.guestRef.phoneNumber ?? '')

  const { mutate: saveSireCodes, isPending: isSavingSire } = useMutation({
    mutationFn: () =>
      reservationGuestApi.update({
        id: guest.id,
        sireEntryConfirmationCode: entryCode || null,
        sireExitConfirmationCode: exitCode || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() })
    },
  })

  const { mutate: saveContact, isPending: isSavingContact } = useMutation({
    mutationFn: () =>
      updateContact({
        reservationGuestId: guest.id,
        contact: {
          firstName: contactFirstName,
          lastName: contactLastName,
          email: contactEmail || null,
          phoneNumber: contactPhone || null,
        },
      }),
    onSuccess: () => {
      // A duplicate-phase save created a new guest entry; this card keeps the
      // filed values, so drop the unsaved edits.
      if (isDuplicatePhase) {
        setContactFirstName(guest.guestRef.firstName)
        setContactLastName(guest.guestRef.lastName)
        setContactPhone(guest.guestRef.phoneNumber ?? '')
        setContactEmail(guest.guestRef.email ?? '')
      }
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() })
    },
  })

  const { mutate: deleteGuest, isPending: isDeletingGuest } = useMutation({
    mutationFn: ({ mode, confirmed }: { mode: GuestDeleteMode; confirmed: boolean }) =>
      deleteGuestCall({ reservationGuestId: guest.id, mode, confirmed }),
    onSuccess: (_, { mode }) => {
      setConfirmDeleteOpen(false)
      setDeleteConflict(null)
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() })
      if (mode === guestDeleteModeEnum.PERMANENT) setIsRemoved(true)
    },
    onError: (error) => {
      const conflict = guestDeleteConflictFromError(error)
      if (!conflict) return
      setDeleteConflict(conflict)
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() })
    },
  })

  const handleSubmitTra = () => {
    submitTra({ reservationGuestId: guest.id })
  }

  const handleConfirmSire = () => {
    confirmSire({ reservationGuestId: guest.id })
  }

  const handleUnconfirmSire = () => {
    unconfirmSire({ reservationGuestId: guest.id })
  }

  const isSubmitting = guest.traStatus === TraStatus.submitting || isJobActive
  const isColombia = propertyCountry === 'CO'
  const traDisabled = !reportsTra
  const sireDisabled = !reportsSire
  const notConfiguredTooltip = t('reporting.notConfiguredTooltip')
  // Distinct from the not-configured gate: a configured property still can't
  // submit TRA/SIRE until the guest's survey is actually completed.
  const surveyIncomplete = !guest.surveyCompleted
  const surveyIncompleteTooltip = t('reporting.surveyIncompleteTooltip')
  // Calendar-synced reservations lack price / guest count; TRA and SIRE refuse them.
  const detailsMissing = missingBookingDetails.length > 0
  const reportBlocked = surveyIncomplete || detailsMissing
  const reportBlockedTooltip = detailsMissing
    ? t('bookingDetails.calendarSyncHint')
    : surveyIncomplete
    ? surveyIncompleteTooltip
    : undefined
  // Disabled tooltips name the action first, then why it's unavailable.
  const blockedTooltip = (action: string) =>
    reportBlockedTooltip
      ? t('common.actionWithReason', { action, reason: reportBlockedTooltip })
      : undefined

  const contactEditable = guest.editPhase !== editPhaseEnum.LOCKED
  const isDuplicatePhase = guest.editPhase === editPhaseEnum.DUPLICATE
  const canEscalate =
    guest.complianceStatus === complianceStatusEnum.SURVEY_PENDING &&
    !guest.formInstance &&
    !guest.dataScrubbed

  const contactChanged =
    contactFirstName !== guest.guestRef.firstName ||
    contactLastName !== guest.guestRef.lastName ||
    contactEmail !== (guest.guestRef.email ?? '') ||
    contactPhone !== (guest.guestRef.phoneNumber ?? '')
  const contactPhoneInvalid = Boolean(contactPhone) && !isValidPhone(contactPhone)

  const codesChanged =
    entryCode !== (guest.sireEntryConfirmationCode ?? '') ||
    exitCode !== (guest.sireExitConfirmationCode ?? '')

  if (isRemoved) return null

  return (
    <div
      className="flex flex-col divide-y divide-border rounded border border-border bg-surface shadow-sm"
      data-testid={`guest-card-${guest.id}`}
    >
      {/* Headline row */}
      <div className="flex items-center gap-2 p-3" data-testid="guest-card-headline">
        {guest.dataScrubbed ? (
          <span className="font-semibold text-text-primary">
            {guest.guestRef.firstName} {guest.guestRef.lastName}
          </span>
        ) : (
          <Link
            to={`/reservations/${reservationId}/guests/guest/${guest.id}/${guest.formInstance}`}
            className="font-semibold text-text-primary hover:underline"
          >
            {guest.guestRef.firstName} {guest.guestRef.lastName}
          </Link>
        )}
        {guest.isBooker && (
          <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-700">
            {t('reservationGuestCard.booker')}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {isColombia && <TraStatusIcon guest={guest} isJobActive={isJobActive} />}
          {isColombia && <SireStatusIcon guest={guest} />}
          <Button
            variant="ghost"
            tone="error"
            size="sm"
            iconOnly
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            tooltip={t('reservationGuestCard.deleteSurvey')}
            data-testid="guest-delete-button"
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Accordion sections */}
      <div className="flex flex-col divide-y divide-border px-3">
        {/* Contact section */}
        <AccordionSection
          title={t('reservationGuestCard.contactSection')}
          data-testid="accordion-contact"
        >
          {contactEditable ? (
            <div className="flex flex-col gap-1.5 text-sm" data-testid="contact-editable">
              {isDuplicatePhase && (
                <InfoHint
                  text={t('reservationGuestCard.duplicateEditHint')}
                  data-testid="contact-duplicate-hint"
                />
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={contactFirstName}
                  onChange={(e) => setContactFirstName(e.target.value)}
                  placeholder={t('reservationGuestCard.firstNamePlaceholder')}
                  className="w-full rounded-md border bg-surface p-1.5 text-sm"
                  data-testid="contact-first-name"
                />
                <input
                  type="text"
                  value={contactLastName}
                  onChange={(e) => setContactLastName(e.target.value)}
                  placeholder={t('reservationGuestCard.lastNamePlaceholder')}
                  className="w-full rounded-md border bg-surface p-1.5 text-sm"
                  data-testid="contact-last-name"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <EnvelopeIcon className="h-3.5 w-3.5 text-text-secondary" aria-hidden="true" />
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder={t('reservationGuestCard.emailPlaceholder')}
                  className="w-full rounded-md border bg-surface p-1.5 text-sm"
                  data-testid="contact-email"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <PhoneIcon className="h-3.5 w-3.5 text-text-secondary" aria-hidden="true" />
                <PhoneNumberInput
                  value={contactPhone || undefined}
                  onChange={(value) => setContactPhone(value ?? '')}
                  placeholder={t('reservationGuestCard.phonePlaceholder')}
                  className="text-sm"
                  data-testid="contact-phone"
                />
              </div>
              {contactPhoneInvalid && (
                <p className="text-xs text-error" data-testid="contact-phone-error">
                  {t('common.invalidPhone')}
                </p>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveContact()}
                disabled={isSavingContact || !contactChanged || contactPhoneInvalid}
                className="self-end"
                data-testid="contact-save"
              >
                {isSavingContact ? <Spinner size="xs" /> : t('common.save')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 text-sm" data-testid="contact-readonly">
              <div className="text-text-primary">
                {guest.guestRef.firstName} {guest.guestRef.lastName}
              </div>
              {guest.guestRef.email && (
                <div className="flex items-center gap-1.5 text-text-secondary">
                  <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{guest.guestRef.email}</span>
                </div>
              )}
              {guest.guestRef.phoneNumber && (
                <div className="flex items-center gap-1.5 text-text-secondary">
                  <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{guest.guestRef.phoneNumber}</span>
                </div>
              )}
              <div
                className="flex flex-col gap-1 text-xs text-text-placeholder"
                title={t('reservationGuestCard.editLockHint')}
                data-testid="contact-lock-hint"
              >
                <div className="flex items-center gap-1">
                  <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>
                    {guest.dataScrubbed
                      ? t('reservationGuestCard.lockScrubbedTitle')
                      : t('reservationGuestCard.lockReportedTitle')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </AccordionSection>

        {/* Survey section */}
        <AccordionSection
          title={t('reservationGuestCard.surveySection')}
          data-testid="accordion-survey"
        >
          {guest.dataScrubbed ? (
            <div
              className="rounded-md bg-surface-hover px-3 py-2 text-xs text-text-secondary"
              data-testid="survey-scrubbed"
            >
              {t('reservationGuestCard.dataScrubbed')}
            </div>
          ) : (
            <GuestSurveyReview guest={guest} />
          )}
        </AccordionSection>

        {/* TRA section — only for Colombian properties */}
        {isColombia && (
          <AccordionSection
            title={t('reservationGuestCard.traSection')}
            data-testid="accordion-tra"
          >
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-text-secondary">
                    {t('reservationGuestCard.statusLabel')}
                  </span>
                  {traDisabled ? (
                    <span className="text-text-placeholder" title={notConfiguredTooltip}>
                      {t('reservationGuestCard.notConfigured')}
                    </span>
                  ) : (
                    <>
                      {guest.traStatus === TraStatus.not_started && !isJobActive && (
                        <span className="text-text-placeholder">
                          {t('reservationGuestCard.notStarted')}
                        </span>
                      )}
                      {isSubmitting && (
                        <span className="flex items-center gap-1 text-text-secondary">
                          <Spinner size="xs" /> {t('reservationGuestCard.submitting')}
                        </span>
                      )}
                      {guest.traStatus === TraStatus.success && !isJobActive && (
                        <span className="text-green-600">{t('reservationGuestCard.success')}</span>
                      )}
                      {guest.traStatus === TraStatus.failed && !isJobActive && (
                        <span className="text-error">{t('reservationGuestCard.failed')}</span>
                      )}
                    </>
                  )}
                </div>
                {!guest.dataScrubbed && (
                  <Button
                    onClick={() => onShowTra(guest)}
                    disabled={!guest.traData}
                    variant="ghost"
                    tone="neutral"
                    size="sm"
                    iconOnly
                    tooltip={t('reservationGuestCard.viewTra')}
                    data-testid="tra-view-button"
                  >
                    <EyeIcon className="h-5 w-5 text-text-primary" aria-hidden="true" />
                  </Button>
                )}
              </div>
              {!traDisabled && <MissingBookingDetailsNotice missing={missingBookingDetails} />}
              {!traDisabled && (
                <div className="flex items-center gap-2">
                  {guest.traStatus === TraStatus.not_started && !isJobActive && (
                    <Button
                      onClick={handleSubmitTra}
                      variant="primary"
                      className="text-xs"
                      disabled={reportBlocked}
                      tooltip={blockedTooltip(t('reservationGuestCard.submitTra'))}
                      data-testid="tra-submit"
                    >
                      <PlayCircleIcon className="mr-1 h-4 w-4" />{' '}
                      {t('reservationGuestCard.submitTra')}
                    </Button>
                  )}
                  {guest.traStatus === TraStatus.failed && !isJobActive && (
                    <Button
                      onClick={handleSubmitTra}
                      variant="danger"
                      className="text-xs"
                      disabled={reportBlocked}
                      tooltip={blockedTooltip(t('reservationGuestCard.retryTra'))}
                      data-testid="tra-submit"
                    >
                      {t('reservationGuestCard.retryTra')}
                    </Button>
                  )}
                  {guest.traStatus === TraStatus.success && !isJobActive && (
                    <Button
                      onClick={handleSubmitTra}
                      variant="ghost"
                      className="text-xs"
                      disabled={reportBlocked}
                      tooltip={
                        blockedTooltip(t('reporting.resubmitTra')) ?? t('reporting.resubmitTra')
                      }
                      data-testid="tra-resubmit"
                    >
                      <ArrowPathIcon className="mr-1 h-4 w-4" />{' '}
                      {t('reservationGuestCard.resubmit')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </AccordionSection>
        )}

        {/* SIRE section — only for Colombian properties */}
        {isColombia && (
          <AccordionSection
            title={t('reservationGuestCard.sireSection')}
            data-testid="accordion-sire"
          >
            <div className="flex flex-col gap-2 text-sm">
              {!sireDisabled && !guest.dataScrubbed && (
                <MissingBookingDetailsNotice missing={missingBookingDetails} />
              )}
              {guest.needsSire && !guest.sireSubmitted && checkoutDate && (
                <SireRetentionBanner
                  checkoutDate={checkoutDate}
                  dataScrubbed={guest.dataScrubbed}
                />
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {guest.needsSire ? (
                    <img
                      src={sireImg}
                      alt={t('reservationGuestCard.sireAlt')}
                      className="h-5 w-5"
                      title={t('reservationGuestCard.sireRequired')}
                    />
                  ) : (
                    <span className="text-text-tertiary">
                      {t('reservationGuestCard.notRequired')}
                    </span>
                  )}
                </div>
                {!guest.dataScrubbed && (
                  <>
                    {sireDisabled ? (
                      <Button
                        variant="ghost"
                        tone="neutral"
                        size="sm"
                        iconOnly
                        disabled
                        aria-label={t('reservationGuestCard.downloadSire')}
                        tooltip={t('common.actionWithReason', {
                          action: t('reservationGuestCard.downloadSire'),
                          reason: notConfiguredTooltip,
                        })}
                        data-testid="sire-download-disabled"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 text-gray-300" aria-hidden="true" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => downloadSireForGuest(guest.id)}
                        disabled={reportBlocked}
                        aria-label={t('reservationGuestCard.downloadSire')}
                        tooltip={
                          blockedTooltip(t('reservationGuestCard.downloadSire')) ??
                          t('reservationGuestCard.downloadSire')
                        }
                        variant="ghost"
                        tone="neutral"
                        size="sm"
                        iconOnly
                        data-testid="sire-download"
                      >
                        <ArrowDownTrayIcon
                          className="h-5 w-5 text-text-primary"
                          aria-hidden="true"
                        />
                      </Button>
                    )}
                  </>
                )}
              </div>
              {guest.needsSire && !sireDisabled && !guest.dataScrubbed && (
                <div
                  className="flex items-center justify-between gap-2"
                  data-testid="sire-confirm-section"
                >
                  {guest.sireSubmitted ? (
                    <>
                      <span
                        className="flex items-center gap-1 text-green-600"
                        data-testid="sire-confirmed-label"
                      >
                        <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                        {t('reservationGuestCard.sireConfirmedManually')}
                      </span>
                      <Button
                        onClick={handleUnconfirmSire}
                        variant="ghost"
                        tone="neutral"
                        className="text-xs"
                        disabled={isUnconfirmingSire}
                        data-testid="sire-unconfirm"
                      >
                        {isUnconfirmingSire ? (
                          <Spinner size="xs" />
                        ) : (
                          t('reservationGuestCard.unconfirmSireSubmitted')
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleConfirmSire}
                      variant="primary"
                      className="text-xs"
                      disabled={isConfirmingSire || reportBlocked}
                      tooltip={blockedTooltip(t('reservationGuestCard.confirmSireSubmitted'))}
                      data-testid="sire-confirm"
                    >
                      {isConfirmingSire ? (
                        <Spinner size="xs" />
                      ) : (
                        t('reservationGuestCard.confirmSireSubmitted')
                      )}
                    </Button>
                  )}
                </div>
              )}
              {guest.needsSire && !sireDisabled && (
                <div className="flex flex-col gap-1.5" data-testid="sire-confirmation-codes">
                  <InfoHint
                    text={t('reservationGuestCard.sireConfirmHelper')}
                    className="self-start"
                    data-testid="sire-confirmation-helper"
                  />
                  <input
                    type="text"
                    placeholder={t('reservationGuestCard.entryCodePlaceholder')}
                    value={entryCode}
                    onChange={(e) => setEntryCode(e.target.value)}
                    className="w-full rounded-md border bg-surface p-1.5 text-sm"
                    data-testid="sire-entry-code"
                  />
                  <input
                    type="text"
                    placeholder={t('reservationGuestCard.exitCodePlaceholder')}
                    value={exitCode}
                    onChange={(e) => setExitCode(e.target.value)}
                    className="w-full rounded-md border bg-surface p-1.5 text-sm"
                    data-testid="sire-exit-code"
                  />
                  <Button
                    variant="primary"
                    onClick={() => saveSireCodes()}
                    disabled={isSavingSire || !codesChanged}
                    className="self-end text-xs"
                    data-testid="sire-save-codes"
                  >
                    {isSavingSire ? <Spinner size="xs" /> : t('common.save')}
                  </Button>
                </div>
              )}
            </div>
          </AccordionSection>
        )}
      </div>

      {canEscalate && (
        <div className="p-3" data-testid="escalate-guest">
          <Button
            variant="secondary"
            size="sm"
            icon={<ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />}
            onClick={() => navigate(`/reservations/${reservationId}/guests?escalate=${guest.id}`)}
            data-testid="escalate-guest-button"
          >
            {t('reservationGuestCard.completeSurvey')}
          </Button>
        </div>
      )}

      {guest.previousContact && !guest.dataScrubbed && (
        <div className="p-3 text-xs text-text-secondary" data-testid="previous-contact">
          {t('reservationGuestCard.previousContact', {
            values: Object.values(guest.previousContact).join(', '),
          })}
        </div>
      )}

      {!guest.dataScrubbed && guest.receiptSystems && guest.receiptSystems.length > 0 && (
        <div className="flex gap-2 p-3" data-testid="receipt-downloads">
          {guest.receiptSystems.map((system) => (
            <Button
              key={system}
              variant="ghost"
              size="sm"
              icon={<ArrowDownTrayIcon className="h-4 w-4" />}
              onClick={() => downloadReceipt(guest.id, system)}
            >
              {t('reservationGuestCard.downloadReceipt', { system: system.toUpperCase() })}
            </Button>
          ))}
        </div>
      )}

      <GuestDeleteDialog
        key={deleteTier}
        isOpen={confirmDeleteOpen}
        tier={deleteTier}
        name={`${guest.guestRef.firstName} ${guest.guestRef.lastName}`.trim()}
        filings={deleteFilings}
        guestId={guest.id}
        receiptSystems={receiptSystems}
        isPending={isDeletingGuest}
        onDelete={(mode, confirmed) => deleteGuest({ mode, confirmed })}
        onCancel={() => {
          setConfirmDeleteOpen(false)
          setDeleteConflict(null)
        }}
      />
    </div>
  )
}
